import type { ComponentType, ReactNode, SVGProps } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { NEUTRAL_BUTTON_CLASS } from '../details/CredsActionButtons';

export function Spinner({ className = 'h-3.5 w-3.5' }: { className?: string }) {
    return (
        <span
            aria-hidden="true"
            className={`inline-block shrink-0 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin ${className}`}
        />
    );
}

interface PaneMessageProps {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    title: string;
    children?: ReactNode;
}

export function PaneMessage({ icon: Icon, title, children }: PaneMessageProps) {
    return (
        <div className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center">
            <Icon className="h-10 w-10 text-gray-300" strokeWidth={1.2} aria-hidden="true" />
            <p className="text-sm font-medium text-gray-600 max-w-[360px]">{title}</p>
            {children}
        </div>
    );
}

interface InlineErrorProps {
    message: string;
    onRetry: () => void;
    retryLabel?: string;
}

export function InlineError({ message, onRetry, retryLabel = 'Retry' }: InlineErrorProps) {
    return (
        <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
        >
            <p className="text-[12px] text-red-700 min-w-0">{message}</p>
            <button type="button" onClick={onRetry} className={NEUTRAL_BUTTON_CLASS}>
                <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {retryLabel}
            </button>
        </div>
    );
}

const SKELETON_WIDTHS = ['w-3/5', 'w-2/5', 'w-1/2', 'w-3/4', 'w-1/3'];

export function SkeletonLines({ count = 4 }: { count?: number }) {
    return (
        <div className="flex flex-col gap-2.5" aria-hidden="true">
            {Array.from({ length: count }, (_, index) => (
                <div
                    key={index}
                    className={`h-3.5 rounded bg-gray-200 animate-pulse ${SKELETON_WIDTHS[index % SKELETON_WIDTHS.length]}`}
                />
            ))}
        </div>
    );
}
